// Finance Imports — drag-drop upload → auto-detect → preview with checkboxes & duplicate warnings → commit.
// Supports: JFF payouts xlsx · OF Sales CSV · OF Payouts PDF · OF Statement PDF.
import React, { useState, useRef, useMemo, useEffect } from "react";
import AppLayout from "../../components/AppLayout";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Upload, FileText, RefreshCw, CheckCircle2, AlertCircle,
  X as XIcon, TrendingUp, FileSpreadsheet, FileImage, AlertTriangle, Sparkles,
} from "lucide-react";

const FORMAT_LABEL = {
  jff_payouts: "JustForFans payouts",
  jff_sales: "JustForFans sales",
  jff_store_sales: "JustForFans store sales",
  jff_top_sellers: "JustForFans top sellers",
  of_sales: "OnlyFans sales",
  of_payouts: "OnlyFans payouts",
  of_statement: "OnlyFans statement",
  of_post_report: "OnlyFans post report",
  of_posts_stats: "OnlyFans posts statistics",
  of_messages_stats: "OnlyFans messages statistics",
  of_payouts_history: "OnlyFans payouts history",
  of_tracking_links: "OnlyFans tracking links (promo)",
  rfc_period: "RFC · Revenue by Period",
  rfc_streaming: "RFC · Streaming (per video)",
  rfc_downloads: "RFC · Downloads (per video)",
  rfc_passes: "RFC · Passes / Rentals",
  rfc_referrals: "RFC · X-Referrals (daily)",
  fh_payouts: "Faphouse · Payouts",
  fh_video_sales: "Faphouse · Video Sales (daily)",
  fh_fanclub: "Faphouse · Fanclub Sales",
  fh_viewshare: "Faphouse · View Share",
  unknown: "Unknown format",
};

export default function FinanceImports() {
  const [preview, setPreview] = useState(null); // { format, rows, summary, filename, duplicate_count, ... }
  const [selected, setSelected] = useState({}); // { rowIdx: true }
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);

  const loadHistory = async () => {
    try {
      const r = await api.get("/api/finance/imports/history");
      setHistory(r.data?.imports || []);
    } catch { /* silent */ }
  };
  React.useEffect(() => { loadHistory(); }, []);

  const [relinking, setRelinking] = useState(false);
  const relinkOfSales = async () => {
    if (!window.confirm("Re-scan all imported OnlyFans sales and link them to CRM contacts by username?")) return;
    setRelinking(true);
    try {
      const r = await api.post("/api/finance/imports/relink-of-sales");
      toast.success(`Scanned ${r.data.scanned} · linked ${r.data.newly_linked} · relinked ${r.data.relinked}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Relink failed");
    } finally { setRelinking(false); }
  };

  const [migrating, setMigrating] = useState(false);
  const migrateBankDeposits = async () => {
    if (!window.confirm(
      "MIGRATE legacy Bank Deposits into the Revenue ledger?\n\n" +
      "• All bank_payouts records will be COPIED (not moved) to `revenue`.\n" +
      "• Idempotent — safe to run more than once.\n" +
      "• Invoice PDFs / receipts are preserved.\n" +
      "• The revenue chart, MRR, and Total Revenue will jump by the migrated amount.\n\n" +
      "Continue?"
    )) return;
    setMigrating(true);
    try {
      const r = await api.post("/api/finance/imports/migrate-bank-payouts-to-revenue");
      toast.success(
        `✓ Migrated ${r.data.migrated} · skipped ${r.data.skipped}${r.data.error_count ? ` · ${r.data.error_count} errors` : ""}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally { setMigrating(false); }
  };

  const [wiping, setWiping] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);
  const autoLinkVideos = async () => {
    const scoreStr = window.prompt(
      "Auto-link ALL unlinked OF/JFF/RFC sales to Catalog videos via fuzzy match?\n\n" +
      "Only rows scoring ≥ threshold will be linked. 0.70 is a safe default.\n" +
      "Type the threshold (0.5–0.95) or blank for 0.70:"
    );
    if (scoreStr === null) return;
    const min = scoreStr.trim() ? parseFloat(scoreStr) : 0.7;
    if (isNaN(min) || min < 0.4 || min >= 1) { toast.error("Invalid threshold — must be 0.4–0.99"); return; }
    setAutoLinking(true);
    try {
      const r = await api.post("/api/finance/imports/video-auto-link", { min_score: min });
      const d = r.data;
      toast.success(`✓ Auto-linked ${d.linked} rows · skipped ${d.skipped} · avg score ${d.avg_score}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-link failed");
    } finally { setAutoLinking(false); }
  };

  const wipeAllFinance = async () => {
    const c1 = window.prompt(
      "⚠️  DANGER: This will WIPE ALL finance data:\n" +
      "  • revenue ledger\n" +
      "  • finance_sales (OF + JFF + RFC per-transaction)\n" +
      "  • finance_top_sellers (JFF + RFC per-video)\n" +
      "  • finance_statements\n" +
      "  • bank_payouts (legacy)\n" +
      "  • finance_imports history\n\n" +
      "This CANNOT be undone. Videos, CRM, and social data are UNTOUCHED.\n\n" +
      "Type WIPE to confirm:"
    );
    if (c1 !== "WIPE") { if (c1 !== null) toast.error("Wipe cancelled — you must type exactly WIPE"); return; }
    setWiping(true);
    try {
      const r = await api.post("/api/finance/imports/wipe-all");
      toast.success(
        `✓ Wiped ${r.data.total_deleted} records across ${Object.keys(r.data.deleted).length} collections`
      );
      loadHistory();
      setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally { setWiping(false); }
  };

  // Handle suggestions — scan finance_sales item text for @handles, propose CRM credits
  const [suggestions, setSuggestions] = useState([]);
  const [suggBusy, setSuggBusy] = useState(false);
  const loadSuggestions = async () => {
    setSuggBusy(true);
    try {
      const r = await api.get("/api/finance/imports/handle-suggestions");
      setSuggestions(r.data?.suggestions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suggestion scan failed");
    } finally { setSuggBusy(false); }
  };
  React.useEffect(() => { loadSuggestions(); }, []);

  const confirmCredit = async (s) => {
    if (!window.confirm(
      `Attribute $${s.gross_usd_total} across ${s.row_count} purchase${s.row_count === 1 ? "" : "s"} to ${s.stage_name}?\n\n` +
      `Handles detected: ${(s.matched_handles || []).map(h => "@" + h).join(", ")}`
    )) return;
    try {
      await api.post("/api/finance/imports/handle-credit", {
        crm_id: s.crm_id,
        row_ids: (s.rows || []).map(r => r.id),
      });
      toast.success(`✓ Credited $${s.gross_usd_total} to ${s.stage_name}`);
      loadSuggestions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Credit failed"); }
  };
  const dismissSuggestion = async (s) => {
    if (!window.confirm(`Dismiss ${s.row_count} suggestion${s.row_count === 1 ? "" : "s"} for ${s.stage_name}? (won't be re-proposed)`)) return;
    try {
      await api.post("/api/finance/imports/handle-credit-dismiss", {
        crm_id: s.crm_id,
        row_ids: (s.rows || []).map(r => r.id),
      });
      toast.success("Dismissed");
      loadSuggestions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Dismiss failed"); }
  };

  // Video-title suggestions — fuzzy match RFC per-video rows to Catalog videos.
  const [videoSuggestions, setVideoSuggestions] = useState([]);
  const [videoSuggBusy, setVideoSuggBusy] = useState(false);
  const [allVideos, setAllVideos] = useState([]);  // for manual picker
  const [pickerOpenFor, setPickerOpenFor] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const loadVideoSuggestions = async () => {
    setVideoSuggBusy(true);
    try {
      const r = await api.get("/api/finance/imports/video-suggestions");
      setVideoSuggestions(r.data?.suggestions || []);
    } catch { /* silent */ } finally { setVideoSuggBusy(false); }
  };
  const loadAllVideos = React.useCallback(async () => {
    try {
      const r = await api.get("/api/videos/");
      setAllVideos((r.data || []).map(v => ({
        id: v.id, title: v.title || "", cast: v.cast || "",
        is_studio: !!v.is_studio, studio_name: v.studio_name_cache || v.studio_name || "",
      })));
    } catch { /* silent */ }
  }, []);
  React.useEffect(() => { loadVideoSuggestions(); loadAllVideos(); }, [loadAllVideos]);
  const confirmVideoLink = async (s, videoOverride = null) => {
    const targetVideoId = videoOverride?.id || s.video_id;
    const targetTitle = videoOverride?.title || s.video_title;
    try {
      await api.post("/api/finance/imports/video-link", {
        record_id: s.record_id || s.top_seller_id,
        source: s.source || "top_seller",
        video_id: targetVideoId,
      });
      toast.success(`✓ Linked "${s.item.slice(0, 40)}" → ${targetTitle.slice(0, 40)}`);
      setVideoSuggestions(prev => prev.filter(x => (x.record_id || x.top_seller_id) !== (s.record_id || s.top_seller_id)));
      setPickerOpenFor(null);
      setPickerSearch("");
      loadConfirmedLinks();
    } catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
  };
  const dismissVideoLink = async (s) => {
    try {
      await api.post("/api/finance/imports/video-link-dismiss", {
        record_id: s.record_id || s.top_seller_id,
        source: s.source || "top_seller",
        video_id: s.video_id,
      });
      setVideoSuggestions(prev => prev.filter(x => (x.record_id || x.top_seller_id) !== (s.record_id || s.top_seller_id)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Dismiss failed"); }
  };

  // Confirmed links — for review + undo
  const [confirmedLinks, setConfirmedLinks] = useState([]);
  const [showConfirmedLinks, setShowConfirmedLinks] = useState(false);
  const loadConfirmedLinks = React.useCallback(async () => {
    try {
      const r = await api.get("/api/finance/imports/video-links");
      setConfirmedLinks(r.data?.links || []);
    } catch { /* silent */ }
  }, []);
  React.useEffect(() => { loadConfirmedLinks(); }, [loadConfirmedLinks]);
  const unlinkVideo = async (row, dismissAfter = false) => {
    if (!window.confirm(
      dismissAfter
        ? `Unlink "${row.item.slice(0, 40)}" from "${row.video_title.slice(0, 40)}" AND never propose this pair again?`
        : `Unlink "${row.item.slice(0, 40)}" from "${row.video_title.slice(0, 40)}"?\n\nIt will re-appear in Video Attributions to be re-confirmed.`
    )) return;
    try {
      await api.post("/api/finance/imports/video-unlink", {
        record_id: row.record_id || row.top_seller_id,
        source: row.source || "top_seller",
        dismiss_current: dismissAfter,
      });
      toast.success(dismissAfter ? "Unlinked + will not be re-proposed" : "Unlinked — moved back to Video Attributions");
      setConfirmedLinks(prev => prev.filter(x => (x.record_id || x.top_seller_id) !== (row.record_id || row.top_seller_id)));
      loadVideoSuggestions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Unlink failed"); }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) {
      toast.error("File too large (max 60MB)");
      return;
    }
    setUploading(true);
    setPreview(null);
    setSelected({});
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/api/finance/imports/detect", fd);
      const data = r.data || {};
      setPreview(data);
      // Default selection: check non-duplicates, uncheck duplicates
      const sel = {};
      (data.rows || []).forEach((row, i) => { sel[i] = !row._is_duplicate; });
      setSelected(sel);
      if (data.format === "unknown") {
        toast.error(data.message || "File format not recognized");
      } else {
        const dup = data.duplicate_count || 0;
        toast.success(
          `Detected: ${FORMAT_LABEL[data.format]} · ${(data.rows || []).length} rows` +
          (dup > 0 ? ` · ${dup} duplicate${dup === 1 ? "" : "s"} flagged` : "")
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Detect failed");
    } finally { setUploading(false); }
  };

  const selectedIndices = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]).map(Number),
    [selected]
  );
  const selectedCount = selectedIndices.length;

  const [ofAccount, setOfAccount] = useState("");  // "vip" | "free" | "" for OF imports
  const [postReportVideoId, setPostReportVideoId] = useState("");  // For of_post_report: pick 1 video
  const [videoOptions, setVideoOptions] = useState([]);  // catalog list for picker

  // Lazy-load catalog videos ONCE when preview switches into of_post_report
  useEffect(() => {
    if (preview?.format === "of_post_report" && videoOptions.length === 0) {
      (async () => {
        try {
          const r = await api.get("/api/videos/?limit=500");
          const rows = Array.isArray(r.data) ? r.data : (r.data?.videos || []);
          rows.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
          setVideoOptions(rows);
        } catch { /* silent — picker just stays empty */ }
      })();
    }
  }, [preview?.format]);  // reload once when format changes

  const commitImport = async () => {
    if (!preview || selectedCount === 0) return;
    const isOfFormat = ["of_sales", "of_payouts", "of_statement", "of_post_report",
                        "of_posts_stats", "of_messages_stats",
                        "of_payouts_history", "of_tracking_links"].includes(preview.format);
    const isPostReport = preview.format === "of_post_report";
    if (isOfFormat && !ofAccount) {
      toast.error("Choose OnlyFans account first — VIP or Free");
      return;
    }
    const dupSelected = selectedIndices.filter((i) => preview.rows[i]?._is_duplicate).length;
    let confirmMsg = `Import ${selectedCount} row${selectedCount === 1 ? "" : "s"} as ${FORMAT_LABEL[preview.format]}${isOfFormat ? ` (${ofAccount.toUpperCase()})` : ""}?`;
    if (isPostReport && postReportVideoId) {
      const vid = videoOptions.find(v => v.id === postReportVideoId);
      confirmMsg += `\n\nLink all transactions to video: "${vid?.title || postReportVideoId}"`;
    } else if (isPostReport && !postReportVideoId) {
      confirmMsg += `\n\n⚠️  No Catalog video linked — you can link later from Leaderboard/Content 360°.`;
    }
    if (dupSelected > 0) {
      confirmMsg += `\n\n⚠️  ${dupSelected} of them look like duplicates of existing records. Continue anyway?`;
    }
    if (!window.confirm(confirmMsg)) return;

    setCommitting(true);
    try {
      const rowsToSend = selectedIndices.map((i) => preview.rows[i]);
      const r = await api.post("/api/finance/imports/commit", {
        format: preview.format,
        filename: preview.filename,
        rows: rowsToSend,
        dedupe_by_reference: false, // user already chose what to import
        period_from: preview.period_from || null,
        period_to: preview.period_to || null,
        of_account: isOfFormat ? ofAccount : null,
        linked_video_id: isPostReport ? (postReportVideoId || null) : null,
        sources: preview.summary?.sources || null,
      });
      toast.success(`✓ Imported ${r.data.inserted} row${r.data.inserted === 1 ? "" : "s"}${r.data.skipped ? ` · ${r.data.skipped} skipped` : ""}`);
      setPreview(null);
      setSelected({});
      setOfAccount("");
      setPostReportVideoId("");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally { setCommitting(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files[0]) handleFile(files[0]);
  };

  const toggleAll = (checked) => {
    if (!preview?.rows) return;
    const sel = {};
    preview.rows.forEach((_, i) => { sel[i] = checked; });
    setSelected(sel);
  };
  const selectOnlyNonDuplicates = () => {
    if (!preview?.rows) return;
    const sel = {};
    preview.rows.forEach((r, i) => { sel[i] = !r._is_duplicate; });
    setSelected(sel);
  };

  // Columns to display (excluding internal _is_duplicate)
  const displayCols = useMemo(() => {
    if (!preview?.rows?.length) return [];
    return Object.keys(preview.rows[0]).filter((k) => !k.startsWith("_"));
  }, [preview]);

  return (
    <AppLayout
      title="Finance Imports"
      subtitle="Drop your platform payout / earnings file — auto-detect format, review rows (duplicates flagged), then commit what you want."
    >
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Toolbar */}
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <a href="/onlyfans/report" data-testid="finance-posts-report-link"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border text-[11px] hover:bg-[color:var(--gio-primary-soft)] transition"
            style={{ borderColor: "var(--gio-primary)", color: "var(--gio-primary)" }}
            title="Auto-parse an OnlyFans Posts Report PDF into structured rows">
            📊 Open Posts Report (PDF auto-parser)
          </a>
          <button onClick={migrateBankDeposits} disabled={migrating}
            data-testid="finance-migrate-bank-deposits"
            className="btn-ghost inline-flex items-center gap-1.5 text-[11px] disabled:opacity-50"
            style={{ borderColor: "#06B6D4", color: "#06B6D4" }}
            title="One-time migration: copy all legacy bank_payouts into the Revenue ledger">
            <TrendingUp size={12} className={migrating ? "animate-pulse" : ""} />
            {migrating ? "Migrating…" : "Migrate legacy Bank Deposits"}
          </button>
          <button onClick={relinkOfSales} disabled={relinking}
            data-testid="finance-relink-of-sales"
            className="btn-ghost inline-flex items-center gap-1.5 text-[11px] disabled:opacity-50"
            title="Re-scan all imported OF sales rows and match usernames to CRM contacts">
            <RefreshCw size={12} className={relinking ? "animate-spin" : ""} />
            {relinking ? "Relinking…" : "Relink OF sales → CRM"}
          </button>
          <button onClick={autoLinkVideos} disabled={autoLinking}
            data-testid="finance-video-auto-link"
            className="btn-ghost inline-flex items-center gap-1.5 text-[11px] disabled:opacity-50"
            style={{ borderColor: "#14b8a6", color: "#14b8a6" }}
            title="Batch fuzzy-match all unlinked OF/JFF/RFC sales to Catalog videos above a chosen score threshold">
            <Sparkles size={12} className={autoLinking ? "animate-pulse" : ""} />
            {autoLinking ? "Auto-linking…" : "🪄 Auto-link videos"}
          </button>
          <button onClick={wipeAllFinance} disabled={wiping}
            data-testid="finance-wipe-all"
            className="btn-ghost inline-flex items-center gap-1.5 text-[11px] disabled:opacity-50"
            style={{ borderColor: "#ef4444", color: "#ef4444" }}
            title="Delete ALL finance data (revenue, sales, statements, imports history). CRM & videos untouched.">
            <AlertTriangle size={12} />
            {wiping ? "Wiping…" : "Wipe ALL finance data"}
          </button>
        </div>

        {/* Handle suggestions — @mentions inside item text → propose revenue credit */}
        {suggestions.length > 0 && (
          <div className="rounded-lg border p-4" data-testid="finance-handle-suggestions"
            style={{ borderColor: "var(--gio-primary)", background: "var(--gio-primary-soft)" }}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="overline text-[color:var(--gio-primary)]">💡 REVENUE ATTRIBUTIONS · needs your OK</div>
                <div className="text-[12px] mt-1" style={{ color: "var(--gio-muted-2)" }}>
                  Found <b style={{ color: "var(--gio-text)" }}>{suggestions.length}</b> CRM contact{suggestions.length === 1 ? "" : "s"} mentioned inside imported item titles.
                  Confirm to credit their revenue in the leaderboard.
                </div>
              </div>
              <button onClick={loadSuggestions} disabled={suggBusy}
                data-testid="finance-suggestions-refresh"
                className="btn-ghost text-[11px] inline-flex items-center gap-1">
                <RefreshCw size={11} className={suggBusy ? "animate-spin" : ""} /> Rescan
              </button>
            </div>
            <div className="space-y-2">
              {suggestions.map(s => (
                <div key={s.crm_id} data-testid={`suggestion-${s.crm_id}`}
                  className="rounded border p-3 flex items-center gap-3 flex-wrap"
                  style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-[14px] truncate" style={{ color: "var(--gio-text)" }}>
                      {s.stage_name}
                    </div>
                    <div className="text-[10px] font-mono-ui mt-0.5 truncate" style={{ color: "var(--gio-muted-2)" }}>
                      matched: {(s.matched_handles || []).map(h => "@" + h).join(", ")}
                    </div>
                    <div className="text-[10px] italic mt-0.5" style={{ color: "var(--gio-muted-3)" }}>
                      Latest: {(s.rows?.[0]?.item || "").slice(0, 90)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[16px] font-bold" style={{ color: "var(--gio-secondary)" }}>
                      ${s.gross_usd_total}
                    </div>
                    <div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>
                      {s.row_count} row{s.row_count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => confirmCredit(s)}
                      data-testid={`suggestion-confirm-${s.crm_id}`}
                      className="btn-primary text-[11px] inline-flex items-center gap-1">
                      <CheckCircle2 size={12} /> Credit
                    </button>
                    <button onClick={() => dismissSuggestion(s)}
                      data-testid={`suggestion-dismiss-${s.crm_id}`}
                      className="btn-ghost text-[11px] inline-flex items-center gap-1">
                      <XIcon size={12} /> Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Video-title fuzzy suggestions — RFC per-video rows → Catalog videos */}
        {videoSuggestions.length > 0 && (
          <div className="rounded-lg border p-4" data-testid="finance-video-suggestions"
            style={{ borderColor: "#a855f7", background: "rgba(168, 85, 247, 0.08)" }}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="overline" style={{ color: "#a855f7" }}>🎬 VIDEO ATTRIBUTIONS · needs your OK</div>
                <div className="text-[12px] mt-1" style={{ color: "var(--gio-muted-2)" }}>
                  Found <b style={{ color: "var(--gio-text)" }}>{videoSuggestions.length}</b> RFC report row{videoSuggestions.length === 1 ? "" : "s"} that fuzzy-match a Catalog video. Confirm to link them.
                </div>
              </div>
              <button onClick={loadVideoSuggestions} disabled={videoSuggBusy}
                data-testid="finance-video-suggestions-refresh"
                className="btn-ghost text-[11px] inline-flex items-center gap-1">
                <RefreshCw size={11} className={videoSuggBusy ? "animate-spin" : ""} /> Rescan
              </button>
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {videoSuggestions.map(s => (
                <div key={s.record_id || s.top_seller_id} data-testid={`videosug-${s.record_id || s.top_seller_id}`}
                  className="rounded border p-3 flex items-center gap-3 flex-wrap"
                  style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider font-mono-ui" style={{ color: "#a855f7" }}>
                      {s.source === "sale"
                        ? `${(s.platform || "").toUpperCase()} · ${s.sale_type || "PPV"}${s.buyer ? ` · ${s.buyer}` : ""}`
                        : `RFC ${s.platform.replace("rawfuckclub_", "")}`}
                      {" · "}
                      <span style={{ color: s.score >= 0.75 ? "#22c55e" : s.score >= 0.55 ? "#eab308" : "#ef4444" }}>
                        {(s.score * 100).toFixed(0)}% match
                      </span>
                    </div>
                    <div className="text-[13px] mt-1 truncate" style={{ color: "var(--gio-text)" }} title={s.item}>
                      <span style={{ color: "var(--gio-muted-3)" }}>Report:</span> {s.item}
                    </div>
                    <div className="text-[13px] truncate" style={{ color: "var(--gio-muted-2)" }} title={s.video_title}>
                      <span style={{ color: "var(--gio-muted-3)" }}>→ Catalog:</span> {s.video_title}
                      {s.video_is_studio && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded" style={{ background: "rgba(168,85,247,0.15)", color: "#a855f7", border: "1px solid #a855f7" }}>STUDIO</span>}
                    </div>
                    {s.video_cast && (
                      <div className="text-[10px] mt-0.5 truncate" style={{ color: "var(--gio-muted-3)" }}>
                        Cast: {s.video_cast}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[14px] font-bold" style={{ color: "#22c55e" }}>
                      ${(s.revenue_usd || 0).toFixed(2)}
                    </div>
                    {s.qty_sold > 0 && (
                      <div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{s.qty_sold} qty</div>
                    )}
                    {s.views > 0 && (
                      <div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{s.views} views</div>
                    )}
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => confirmVideoLink(s)}
                      data-testid={`videosug-confirm-${s.top_seller_id}`}
                      className="btn-primary text-[11px] inline-flex items-center gap-1">
                      <CheckCircle2 size={12} /> Link
                    </button>
                    <button onClick={() => { setPickerOpenFor(pickerOpenFor === s.top_seller_id ? null : s.top_seller_id); setPickerSearch(""); }}
                      data-testid={`videosug-pick-${s.top_seller_id}`}
                      className="btn-ghost text-[11px] inline-flex items-center gap-1"
                      title="Pick a different video manually">
                      <FileText size={12} /> Pick…
                    </button>
                    <button onClick={() => dismissVideoLink(s)}
                      data-testid={`videosug-dismiss-${s.top_seller_id}`}
                      className="btn-ghost text-[11px] inline-flex items-center gap-1">
                      <XIcon size={12} /> Skip
                    </button>
                  </div>
                  {pickerOpenFor === s.top_seller_id && (
                    <div className="w-full mt-2 pt-3 border-t" data-testid={`videosug-picker-${s.top_seller_id}`}
                      style={{ borderColor: "var(--gio-border)" }}>
                      <input type="text" value={pickerSearch}
                        onChange={(e) => setPickerSearch(e.target.value)}
                        placeholder={`Search ${allVideos.length} catalog videos by title or cast…`}
                        autoFocus
                        data-testid={`videosug-picker-input-${s.top_seller_id}`}
                        className="w-full px-3 py-1.5 rounded border text-[12px] mb-2"
                        style={{ background: "var(--gio-surface-hi)", borderColor: "var(--gio-border)", color: "var(--gio-text)" }} />
                      <div className="max-h-[240px] overflow-y-auto space-y-1">
                        {allVideos
                          .filter(v => {
                            if (!pickerSearch) return true;
                            const q = pickerSearch.toLowerCase();
                            return (v.title || "").toLowerCase().includes(q) || (v.cast || "").toLowerCase().includes(q);
                          })
                          .slice(0, 30)
                          .map(v => (
                            <button key={v.id}
                              onClick={() => confirmVideoLink(s, v)}
                              data-testid={`videosug-picker-opt-${v.id}`}
                              className="w-full text-left px-2 py-1.5 rounded border flex items-center justify-between gap-2 hover:bg-[var(--gio-surface-hi)] transition"
                              style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                              <div className="min-w-0 flex-1">
                                <div className="text-[12px] truncate" style={{ color: "var(--gio-text)" }}>{v.title || "(untitled)"}</div>
                                {v.cast && <div className="text-[10px] truncate" style={{ color: "var(--gio-muted-3)" }}>{v.cast}</div>}
                              </div>
                              {v.is_studio && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded shrink-0"
                                  style={{ background: "rgba(168,85,247,0.15)", color: "#a855f7", border: "1px solid #a855f7" }}>
                                  STUDIO
                                </span>
                              )}
                            </button>
                          ))}
                        {allVideos.length > 30 && !pickerSearch && (
                          <div className="text-[10px] text-center py-1 italic" style={{ color: "var(--gio-muted-3)" }}>
                            Type to search among {allVideos.length} videos…
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Confirmed video links — review + undo */}
        {confirmedLinks.length > 0 && (
          <div className="rounded-lg border p-4" data-testid="finance-confirmed-links"
            style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
            <button onClick={() => setShowConfirmedLinks(v => !v)}
              data-testid="finance-confirmed-links-toggle"
              className="w-full flex items-center justify-between text-left">
              <div>
                <div className="overline" style={{ color: "var(--gio-muted-2)" }}>
                  🔗 CONFIRMED LINKS · {confirmedLinks.length}
                </div>
                <div className="text-[11px] mt-1" style={{ color: "var(--gio-muted-3)" }}>
                  Click a row to unlink if you confirmed a wrong match — it will re-appear in Video Attributions.
                </div>
              </div>
              <span className="text-[16px]" style={{ color: "var(--gio-muted-2)" }}>{showConfirmedLinks ? "−" : "+"}</span>
            </button>
            {showConfirmedLinks && (
              <div className="mt-3 space-y-2 max-h-[420px] overflow-y-auto">
                {confirmedLinks.map(row => (
                  <div key={row.record_id || row.top_seller_id} data-testid={`videolink-${row.record_id || row.top_seller_id}`}
                    className="rounded border p-3 flex items-center gap-3 flex-wrap"
                    style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-hi)" }}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider font-mono-ui" style={{ color: "#a855f7" }}>
                        {row.source === "sale"
                          ? `${(row.platform || "").toUpperCase()} · ${row.sale_type || "PPV"}${row.buyer ? ` · ${row.buyer}` : ""}`
                          : `RFC ${(row.platform || "").replace("rawfuckclub_", "")}`}
                      </div>
                      <div className="text-[12px] mt-1 truncate" style={{ color: "var(--gio-text)" }} title={row.item}>
                        <span style={{ color: "var(--gio-muted-3)" }}>Report:</span> {row.item}
                      </div>
                      <div className="text-[12px] truncate" style={{ color: "var(--gio-muted-2)" }} title={row.video_title}>
                        <span style={{ color: "var(--gio-muted-3)" }}>→ Catalog:</span> {row.video_title}
                        {row.video_is_studio && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded" style={{ background: "rgba(168,85,247,0.15)", color: "#a855f7", border: "1px solid #a855f7" }}>STUDIO</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[13px] font-bold" style={{ color: "#22c55e" }}>
                        ${(row.revenue_usd || 0).toFixed(2)}
                      </div>
                      {row.qty_sold > 0 && (
                        <div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{row.qty_sold} qty</div>
                      )}
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => unlinkVideo(row, false)}
                        data-testid={`videolink-unlink-${row.top_seller_id}`}
                        className="btn-ghost text-[11px] inline-flex items-center gap-1">
                        <RefreshCw size={12} /> Unlink
                      </button>
                      <button onClick={() => unlinkVideo(row, true)}
                        data-testid={`videolink-unlink-dismiss-${row.top_seller_id}`}
                        className="btn-ghost text-[11px] inline-flex items-center gap-1"
                        style={{ borderColor: "#ef4444", color: "#ef4444" }}
                        title="Unlink AND never propose this pair again">
                        <XIcon size={12} /> Wrong
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Drop zone */}
        {!preview && (
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
            onDrop={onDrop}
            data-testid="finance-import-dropzone"
            className="border-2 border-dashed rounded-2xl p-14 text-center cursor-pointer transition"
            style={{
              borderColor: dragOver ? "var(--gio-primary)" : "var(--gio-border)",
              background: dragOver ? "var(--gio-primary-soft)" : "var(--gio-surface)",
            }}>
            <input ref={inputRef} type="file"
              accept=".xlsx,.csv,.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,text/csv"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden" data-testid="finance-import-file-input" />
            {uploading ? (
              <>
                <RefreshCw size={40} className="mx-auto animate-spin" style={{ color: "var(--gio-primary)" }} />
                <div className="text-[15px] font-heading mt-4" style={{ color: "var(--gio-primary)" }}>Analyzing file…</div>
              </>
            ) : (
              <>
                <Upload size={44} className="mx-auto mb-3" style={{ color: dragOver ? "var(--gio-primary)" : "var(--gio-muted)" }} />
                <div className="text-[16px] font-heading" style={{ color: "var(--gio-text)" }}>
                  Drop file or click to upload
                </div>
                <div className="text-[12px] mt-2" style={{ color: "var(--gio-muted-2)" }}>
                  Supported: JFF payouts (.xlsx) · JFF Store Sales / Top Sellers (.csv) · OF Sales (.csv) · OF Payouts / Statement / Post Report / Posts&Messages Statistics / Payouts History / Tracking Links (.pdf) · RFC reports: revenue by period, streaming, downloads, passes/rentals, x-referrals (.pdf) · Faphouse: payouts, video sales, fanclub sales, view share (.pdf)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-6 text-[10px]">
                  <FmtChip icon={<FileSpreadsheet size={12} />} label="JFF payouts .xlsx" />
                  <FmtChip icon={<FileText size={12} />} label="JFF Store Sales .csv" />
                  <FmtChip icon={<TrendingUp size={12} />} label="JFF Top Sellers .csv" />
                  <FmtChip icon={<FileText size={12} />} label="OF Sales .csv" />
                  <FmtChip icon={<FileImage size={12} />} label="OF Payouts .pdf" />
                  <FmtChip icon={<TrendingUp size={12} />} label="OF Statement .pdf" />
                  <FmtChip icon={<FileImage size={12} />} label="RFC · revenue by period .pdf" />
                  <FmtChip icon={<FileImage size={12} />} label="RFC · streaming .pdf" />
                  <FmtChip icon={<FileImage size={12} />} label="RFC · downloads .pdf" />
                  <FmtChip icon={<FileImage size={12} />} label="RFC · passes/rentals .pdf" />
                  <FmtChip icon={<FileImage size={12} />} label="RFC · x-referrals .pdf" />
                  <FmtChip icon={<FileImage size={12} />} label="Faphouse · payouts .pdf" />
                  <FmtChip icon={<FileImage size={12} />} label="Faphouse · video sales .pdf" />
                  <FmtChip icon={<FileImage size={12} />} label="Faphouse · fanclub sales .pdf" />
                  <FmtChip icon={<FileImage size={12} />} label="Faphouse · view share .pdf" />
                </div>
              </>
            )}
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div className="rounded-lg border" style={{ borderColor: "var(--gio-border)" }} data-testid="finance-import-preview">
            <div className="p-4 border-b flex items-center justify-between flex-wrap gap-2" style={{ borderColor: "var(--gio-border)" }}>
              <div>
                <div className="text-[10px] font-mono-ui uppercase tracking-wider" style={{ color: "var(--gio-muted-2)" }}>
                  {preview.filename}
                </div>
                <div className="font-heading text-lg mt-0.5 inline-flex items-center gap-2">
                  {preview.format === "unknown"
                    ? <AlertCircle size={16} style={{ color: "#ef4444" }} />
                    : <CheckCircle2 size={16} style={{ color: "var(--gio-primary)" }} />
                  }
                  {FORMAT_LABEL[preview.format]}
                  <span className="text-[11px] font-mono-ui px-1.5 py-0.5 rounded"
                    style={{ background: "var(--gio-surface-hi)", color: "var(--gio-muted-2)" }}>
                    conf {Math.round((preview.confidence || 0) * 100)}%
                  </span>
                  {preview.duplicate_count > 0 && (
                    <span data-testid="finance-preview-dup-badge"
                      className="text-[11px] font-mono-ui px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                      style={{ background: "#f59e0b22", color: "#d97706" }}>
                      <AlertTriangle size={11} /> {preview.duplicate_count} duplicate{preview.duplicate_count === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {["of_sales", "of_payouts", "of_statement", "of_post_report",
                  "of_posts_stats", "of_messages_stats",
                  "of_payouts_history", "of_tracking_links"].includes(preview.format) && (
                  <div className="inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px]"
                    data-testid="finance-of-account-picker"
                    style={{ borderColor: ofAccount ? "#00AFF0" : "#ef4444", background: "var(--gio-surface-hi)" }}>
                    <span style={{ color: "var(--gio-muted-2)" }} className="mr-1">OF Account:</span>
                    <button
                      onClick={() => setOfAccount("vip")}
                      data-testid="finance-of-acct-vip"
                      className="px-2 py-0.5 rounded font-mono-ui uppercase tracking-wider transition"
                      style={{
                        background: ofAccount === "vip" ? "#00AFF0" : "transparent",
                        color: ofAccount === "vip" ? "#000" : "var(--gio-muted-2)",
                        fontWeight: ofAccount === "vip" ? 700 : 400,
                      }}>VIP</button>
                    <button
                      onClick={() => setOfAccount("free")}
                      data-testid="finance-of-acct-free"
                      className="px-2 py-0.5 rounded font-mono-ui uppercase tracking-wider transition"
                      style={{
                        background: ofAccount === "free" ? "#22c55e" : "transparent",
                        color: ofAccount === "free" ? "#000" : "var(--gio-muted-2)",
                        fontWeight: ofAccount === "free" ? 700 : 400,
                      }}>Free</button>
                  </div>
                )}
                {preview.format === "of_post_report" && (
                  <div className="inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px]"
                    data-testid="finance-post-report-video-picker"
                    style={{ borderColor: postReportVideoId ? "#14b8a6" : "var(--gio-border)", background: "var(--gio-surface-hi)", maxWidth: 340 }}>
                    <span style={{ color: "var(--gio-muted-2)" }} className="mr-1">Link video:</span>
                    <select
                      value={postReportVideoId}
                      onChange={(e) => setPostReportVideoId(e.target.value)}
                      data-testid="finance-post-report-video-select"
                      className="bg-transparent outline-none text-[11px] flex-1 min-w-0"
                      style={{ color: "var(--gio-text)", maxWidth: 260 }}>
                      <option value="">— none (skip) —</option>
                      {videoOptions.map(v => (
                        <option key={v.id} value={v.id}>
                          {(v.title || "untitled").slice(0, 60)}{v.shoot_date ? ` · ${v.shoot_date.slice(0, 10)}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <button onClick={() => { setPreview(null); setSelected({}); setOfAccount(""); }} data-testid="finance-preview-cancel"
                  className="btn-ghost text-[11px] inline-flex items-center gap-1">
                  <XIcon size={12} /> Cancel
                </button>
                {preview.format !== "unknown" && preview.rows?.length > 0 && (
                  <button onClick={commitImport} disabled={committing || selectedCount === 0}
                    data-testid="finance-preview-commit"
                    className="btn-primary text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50">
                    <CheckCircle2 size={13} />
                    {committing ? "Saving…" : `Save ${selectedCount} selected`}
                  </button>
                )}
              </div>
            </div>

            {/* Summary cards */}
            {preview.summary && Object.keys(preview.summary).length > 0 && (
              <div className="p-4 border-b space-y-3" style={{ borderColor: "var(--gio-border)" }}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="Rows" value={preview.summary.count || 0} testid="import-stat-count" />
                  {preview.summary.total_gross_usd !== undefined && (
                    <Stat label="Gross" value={`$${(preview.summary.total_gross_usd || 0).toLocaleString()}`} testid="import-stat-gross" />
                  )}
                  {preview.summary.total_net_usd !== undefined && (
                    <Stat label="Net USD" value={`$${(preview.summary.total_net_usd || 0).toLocaleString()}`} testid="import-stat-net-usd" accent />
                  )}
                  {preview.summary.total_net_eur !== undefined && (
                    <Stat label="Net EUR" value={`€${(preview.summary.total_net_eur || 0).toLocaleString()}`} testid="import-stat-net-eur" accent />
                  )}
                  {preview.summary.total_tax_usd !== undefined && (
                    <Stat label="Tax" value={`$${(preview.summary.total_tax_usd || 0).toLocaleString()}`} testid="import-stat-tax" />
                  )}
                  {preview.summary.platform && (
                    <Stat label="Platform" value={preview.summary.platform} testid="import-stat-platform" />
                  )}
                </div>
                {preview.summary.type_breakdown && Object.keys(preview.summary.type_breakdown).length > 0 && (
                  <TypeBreakdown breakdown={preview.summary.type_breakdown} />
                )}
              </div>
            )}

            {/* Selection toolbar */}
            {preview.rows?.length > 0 && (
              <div className="px-4 py-2 border-b flex items-center justify-between flex-wrap gap-2 text-[11px]"
                style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-hi)" }}>
                <div style={{ color: "var(--gio-muted-2)" }} data-testid="finance-selection-info">
                  <b style={{ color: "var(--gio-text)" }}>{selectedCount}</b> of {preview.rows.length} selected
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleAll(true)} data-testid="finance-select-all" className="btn-ghost text-[11px]">Select all</button>
                  <button onClick={() => toggleAll(false)} data-testid="finance-select-none" className="btn-ghost text-[11px]">Select none</button>
                  {preview.duplicate_count > 0 && (
                    <button onClick={selectOnlyNonDuplicates} data-testid="finance-select-non-dupes" className="btn-ghost text-[11px]">
                      Only non-duplicates
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Rows preview — FULL LIST with checkboxes */}
            <div className="p-4">
              {preview.rows?.length === 0 && (
                <div className="text-center py-8 text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>
                  No rows parsed.
                </div>
              )}
              {preview.rows?.length > 0 && (
                <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                  <table className="w-full text-[11px]" data-testid="finance-preview-table">
                    <thead className="sticky top-0" style={{ background: "var(--gio-surface)" }}>
                      <tr className="border-b" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}>
                        <th className="text-left py-2 px-2 w-8">
                          <input type="checkbox"
                            checked={selectedCount === preview.rows.length && preview.rows.length > 0}
                            onChange={(e) => toggleAll(e.target.checked)}
                            data-testid="finance-header-checkbox" />
                        </th>
                        <th className="text-left py-2 px-2 w-8" />
                        {displayCols.slice(0, 8).map(k => (
                          <th key={k} className="text-left py-2 px-2 font-mono-ui uppercase tracking-wider">{k.replace(/_/g, " ")}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r, i) => {
                        const isDup = !!r._is_duplicate;
                        return (
                          <tr key={i} className="border-b hover:bg-[color:var(--gio-primary-soft)]"
                            style={{
                              borderColor: "var(--gio-border)",
                              background: isDup ? "#f59e0b0d" : undefined,
                            }}
                            data-testid={`finance-row-${i}`}>
                            <td className="py-1.5 px-2">
                              <input type="checkbox"
                                checked={!!selected[i]}
                                onChange={(e) => setSelected(s => ({ ...s, [i]: e.target.checked }))}
                                data-testid={`finance-row-checkbox-${i}`} />
                            </td>
                            <td className="py-1.5 px-2">
                              {isDup && (
                                <span title="Looks like a duplicate of an existing record" data-testid={`finance-row-dup-${i}`}>
                                  <AlertTriangle size={12} style={{ color: "#d97706" }} />
                                </span>
                              )}
                            </td>
                            {displayCols.slice(0, 8).map(k => (
                              <td key={k} className="py-1.5 px-2 truncate max-w-[220px]" style={{ color: "var(--gio-text)" }}>
                                {typeof r[k] === "number"
                                  ? (k.includes("eur") ? `€${r[k]}` : (k.includes("usd") || k === "amount") ? `$${r[k]}` : r[k])
                                  : String(r[k] ?? "—")}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Import history */}
        {history.length > 0 && (
          <div className="rounded-lg border" style={{ borderColor: "var(--gio-border)" }} data-testid="finance-import-history">
            <div className="p-3 border-b" style={{ borderColor: "var(--gio-border)" }}>
              <div className="overline">Recent imports</div>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--gio-border)" }}>
              {history.slice(0, 10).map(h => (
                <div key={h.id} className="p-3 flex items-center justify-between text-[11px]" data-testid={`import-history-${h.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span style={{ color: "var(--gio-primary)" }}>{FORMAT_LABEL[h.format] || h.format}</span>
                    <span className="truncate" style={{ color: "var(--gio-muted-2)" }}>{h.filename}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0" style={{ color: "var(--gio-muted-3)" }}>
                    <span>✓ {h.inserted}</span>
                    {h.skipped > 0 && <span>⇢ {h.skipped}</span>}
                    <span>{(h.created_at || "").slice(0, 10)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </AppLayout>
  );
}

function TypeBreakdown({ breakdown }) {
  const BUCKET_META = {
    subscription:     { label: "Subscription",     color: "#10b981", desc: "Recurring renewals" },
    app_subscription: { label: "App Subscription", color: "#8b5cf6", desc: "Mobile-app purchases" },
    tip:              { label: "Tips",             color: "#f59e0b", desc: "Fan tips" },
    chargeback:       { label: "Chargebacks",      color: "#ef4444", desc: "Refunds / negative amounts" },
    cancelled:        { label: "Cancelled",        color: "#6b7280", desc: "Ended subscriptions" },
    other:            { label: "Other",            color: "#0ea5e9", desc: "Unclassified" },
  };
  // Preferred order: revenue first, chargebacks last
  const order = ["subscription", "app_subscription", "tip", "cancelled", "other", "chargeback"];
  const keys = order.filter(k => breakdown[k]);
  if (!keys.length) return null;
  return (
    <div data-testid="import-type-breakdown">
      <div className="text-[10px] font-mono-ui uppercase tracking-wider mb-2"
        style={{ color: "var(--gio-muted-2)" }}>
        Breakdown by type
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {keys.map(k => {
          const meta = BUCKET_META[k] || { label: k, color: "#6b7280", desc: "" };
          const v = breakdown[k];
          const isNegative = (v.gross_usd || 0) < 0;
          return (
            <div key={k} data-testid={`type-bucket-${k}`}
              className="rounded border p-2.5"
              style={{
                borderColor: meta.color,
                borderLeftWidth: 3,
                background: "var(--gio-surface-hi)",
              }}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-heading" style={{ color: meta.color }}>
                  {meta.label}
                </div>
                <div className="text-[9px] font-mono-ui" style={{ color: "var(--gio-muted-3)" }}>
                  ×{v.count}
                </div>
              </div>
              <div className="text-[15px] font-bold mt-1"
                style={{ color: isNegative ? "#ef4444" : "var(--gio-text)" }}>
                {isNegative ? "-" : ""}${Math.abs(v.gross_usd || 0).toLocaleString()}
              </div>
              <div className="text-[9px] italic" style={{ color: "var(--gio-muted-3)" }}>
                {meta.desc}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FmtChip({ icon, label }) {
  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded border"
      style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)", background: "var(--gio-surface-hi)" }}>
      <span style={{ color: "var(--gio-primary)" }}>{icon}</span>
      {label}
    </div>
  );
}

function Stat({ label, value, testid, accent }) {
  return (
    <div data-testid={testid} className="rounded border p-2.5"
      style={{
        borderColor: accent ? "var(--gio-secondary)" : "var(--gio-border)",
        background: "var(--gio-surface-hi)",
      }}>
      <div className="text-[9px] font-mono-ui uppercase tracking-wider" style={{ color: "var(--gio-muted-2)" }}>{label}</div>
      <div className="text-[17px] font-bold mt-0.5" style={{ color: accent ? "var(--gio-secondary)" : "var(--gio-text)" }}>
        {value}
      </div>
    </div>
  );
}
